<template>
  <div class="dpt_container">
    <ul class="dpt_tab_list nav nav-tabs" role="tablist">
      <li v-for="t in visibleTabs" :key="t.key" class="nav-item" role="presentation">
        <button
            type="button"
            class="nav-link"
            :class="{ active: activeTab === t.key }"
            @click="changeTab(t.key)"
        >
          {{ t.label }}
        </button>
      </li>
    </ul>

    <div class="dpt_tab_content">
      <transition name="dpt-fade" mode="out-in">
        <keep-alive>
          <component
              :is="currentComp"
              :key="activeTab"
              v-bind="childProps"
          />
        </keep-alive>
      </transition>
    </div>
  </div>
</template>

<script>
import DetailedProjectCommonInfo from "@/components/common/components/DetailedProjectCommonInfo.vue";
import DetailedProjectCollaterals from "@/components/common/components/DetailedProjectCollaterals.vue";
import DetailedProjectFinancials from "@/components/common/components/DetailedProjectFinancials.vue";
import DetailedProjectDocuments from "@/components/common/components/DetailedProjectDocuments.vue";
import PaymentSchedule from "@/components/common/components/PaymentSchedule.vue";
import DetailedProjectOverdue from "@/components/common/components/DetailedProjectOverdue.vue";

export default {
  components: {
    DetailedProjectCommonInfo,
    DetailedProjectCollaterals,
    DetailedProjectFinancials,
    DetailedProjectDocuments,
    PaymentSchedule,
    DetailedProjectOverdue
  },
  props: {
    project: { type: Object, required: true },
    isFundRaising: { type: Boolean, required: true },
    secondaryMarket: { type: Boolean, required: true },
    siteUrlHost: { type: String, default: "" },
    buy: { type: Boolean, default: true }
  },
  data() {
    return {
      activeTab: "common",
      tabs: [
        { key: "common", label: "Общая информация", component: "DetailedProjectCommonInfo", show: () => true },
        { key: "collaterals", label: "Обеспечение", component: "DetailedProjectCollaterals", show: () => true },
        { key: "financials", label: "Финансовая информация", component: "DetailedProjectFinancials", show: (p) => !!p.financials },
        { key: "documents", label: "Документы", component: "DetailedProjectDocuments", show: (p) => !!p.documents },
        { key: "payment-schedule", label: "График платежей", component: "PaymentSchedule", show: (p) => !!p.documents },
        { key: "overdue", label: "Работа с просрочкой", component: "DetailedProjectOverdue", show: () => true }
      ]
    };
  },
  computed: {
    visibleTabs() {
      return this.tabs.filter(t => t.show(this.project));
    },
    currentComp() {
      const tab = this.tabs.find(t => t.key === this.activeTab);
      return tab ? tab.component : "DetailedProjectCommonInfo";
    },
    lastPaymentUuid() {
      const payments = this.project?.loan?.paymentSchedule?.payments;
      if (!Array.isArray(payments)) {
        return undefined;
      }
      let lastUuid;
      for (const payment of payments) {
        if (payment.paidAt) {
          lastUuid = payment.uuid;
        }
      }
      return lastUuid;
    },
    childProps() {
      return {
        project: this.project,
        secondaryMarket: this.secondaryMarket,
        isFundRaising: this.isFundRaising,
        siteUrlHost: this.siteUrlHost,
        buy: this.buy,
        financials: this.project.financials,
        documents: this.project.documents,
        paymentSchedule: this.project?.loan?.paymentSchedule,
        projectStatus: this.project?.status,
        projectType: this.project?.type,
        lastPaymentUuid: this.lastPaymentUuid,
        buybackDate: this.project?.loan?.buybackDate,
      };
    }
  },
  mounted() {
    const q = this.$route?.query?.tab;
    if (q && this.tabs.some(t => t.key === q)) {
      this.activeTab = q;
    } else if (!q) {
      this.updateUrl("common");
    }
  },
  watch: {
    "$route.query.tab"(tab) {
      if (tab && this.tabs.some(t => t.key === tab)) {
        this.activeTab = tab;
      }
    }
  },
  methods: {
    changeTab(tab) {
      if (tab === this.activeTab) return;
      this.activeTab = tab;
      this.updateUrl(tab);
    },
    updateUrl(tab) {
      this.$router.replace({
        query: { ...this.$route.query, tab },
      });
    }
  }
};
</script>

<style lang="scss">
.dpt_container {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  .h6 {
    margin-top: 1.5rem;
  }

  dl {
    dt {
      flex: 0 0 35%;
      max-width: 35%;
    }

    dd {
      flex: 0 0 60%;
      max-width: 35rem;

      a {
        float: right;
      }
    }
  }

  p {
    margin-bottom: 0.25em;
  }

  .nav-tabs {
    border: none;

    .nav-link,
    .nav-link:hover,
    .nav-link:focus,
    .nav-link.active {
      position: relative;
      border: none;
      background: none;
    }

    .nav-link {
      color: #000;
      margin-left: 0;
      margin-right: 0;
      white-space: normal;
      text-align: center;

      @media (max-width: 767.98px) {
        white-space: nowrap;
      }

      @media (min-width: 768px) {
        margin-left: 0.25rem;
        margin-right: 0.25rem;
      }

      @media (min-width: 1040px) {
        margin-left: 0.5rem;
        margin-right: 0.5rem;
      }

      @media (min-width: 1140px) {
        margin-left: 1rem;
        margin-right: 1rem;
      }
    }

    .nav-link.active:before {
      content: "";
      background: var(--primary-green-color);
      position: absolute;
      bottom: 0;
      left: 0;
      height: 0.15rem;
      width: 100%;
    }
  }
}
.dpt_tab_list {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: space-between;
  align-items: stretch;
  gap: 0.5rem;
  padding: 1rem 2rem;
  border: none;
  border-radius: 0.8rem;
  background-color: white;
  color: var(--secondary-gray);
  margin: 0;

  .nav-item {
    display: flex;
    flex: 1 1 0;
    min-width: 0;
  }

  .nav-item .nav-link {
    width: 100%;
  }

  @media (max-width: 767.98px) {
    flex-wrap: nowrap;
    justify-content: flex-start;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    padding: 0.75rem 1rem;

    .nav-item {
      flex: 0 0 auto;
    }
  }
}
.dpt_tab_content {
  padding: 1rem 2rem;
  border: none;
  border-radius: 0.8rem;
  background-color: white;
  min-height: 15rem;
  margin-bottom: 1rem;
}

.dpt-fade-enter-active,
.dpt-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.dpt-fade-enter-from {
  opacity: 0;
  transform: translateY(0.5rem);
}
.dpt-fade-leave-to {
  opacity: 0;
  transform: translateY(-0.25rem);
}
</style>
